import { useEffect, useState } from "react";
import { signInWithGoogle, signOutOfGoogle } from "./auth";
import { NOISE_CLASSES, NOISE_PERIODS } from "./noise";
import { POLLEN_LINKS, POLLEN_SITES, inPollenSeason } from "./pollen";
import { ROADS_SOURCE_URL, groupNotices, isIcy } from "./roads";
import { LEVELS, NO_DATA } from "./severity";
import { SOIL_SOURCE_URL } from "./soil";

const STORAGE_KEY = "loftgaedi.layers";

const DEFAULT_LAYERS = {
  air: true,
  drift: true,
  wind: true,
  pollen: false,
  noise: null, // null (off), "day" (Lden) or "night" (Ln)
  soil: false,
  roads: false,
  sale: false,
  salePostcodes: "105",
  collapsed: false,
};

// Layer choices are a per-viewer convenience: remembered in this browser when
// storage is available, and the defaults otherwise
export const useLayers = () => {
  const [layers, setLayers] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return { ...DEFAULT_LAYERS, ...stored };
    } catch {
      return DEFAULT_LAYERS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layers));
    } catch {
      // Private mode or blocked storage: choices last for this visit only
    }
  }, [layers]);

  const setLayer = (name, value) =>
    setLayers((current) => ({ ...current, [name]: value }));

  return [layers, setLayer];
};

const Switch = ({ checked, onChange, children, small }) => (
  <label className={`aq-switch${small ? " aq-switch--small" : ""}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="aq-switch__track" aria-hidden="true" />
    {children}
  </label>
);

// One layer: a switch with an optional count, and its key and options
// underneath while it is on
const Layer = ({ label, meta, checked, onChange, children }) => (
  <li className={`aq-layer${checked ? " is-on" : ""}`}>
    <div className="aq-layer__head">
      <Switch checked={checked} onChange={onChange}>
        <span className="aq-layer__label">{label}</span>
      </Switch>
      {meta && <span className="aq-mono aq-layer__meta">{meta}</span>}
    </div>
    {checked && children && <div className="aq-layer__detail">{children}</div>}
  </li>
);

// The six levels as a bar, like the noise scale, then a count of only the
// levels present right now
const SeverityKey = ({ counts }) => {
  const present = [...LEVELS, NO_DATA].filter((level) => counts[level.label]);
  return (
    <>
      <div className="aq-severity-key">
        <ol className="aq-severity-key__bar" aria-hidden="true">
          {LEVELS.map((level) => (
            <li key={level.label} style={{ background: level.color }} />
          ))}
        </ol>
        <div className="aq-severity-key__ends" aria-hidden="true">
          <span>{LEVELS[0].label}</span>
          <span>{LEVELS[LEVELS.length - 1].label}</span>
        </div>
      </div>
      {present.length > 0 && (
        <p className="aq-layer__note">
          {present.map((level, i) => (
            <span key={level.label}>
              {i > 0 && " · "}
              <span className="aq-mono">{counts[level.label]}</span>{" "}
              {level.label.toLowerCase()}
            </span>
          ))}
        </p>
      )}
    </>
  );
};

const NoiseKey = ({ period, onPeriod }) => (
  <>
    <div className="aq-segmented" role="group" aria-label="Noise period">
      {Object.entries(NOISE_PERIODS).map(([value, { label }]) => (
        <button
          key={value}
          type="button"
          aria-pressed={period === value}
          onClick={() => onPeriod(value)}
        >
          {label}
        </button>
      ))}
    </div>
    <ol className="aq-noise__scale" aria-label="Noise level, dB">
      {NOISE_CLASSES.map((c) => (
        <li key={c.label}>
          <span style={{ background: c.color }} aria-hidden="true" />
          {c.label}
        </li>
      ))}
    </ol>
    <p className="aq-layer__note">
      dB {NOISE_PERIODS[period].unit}, 2022. Mapped towns only.
    </p>
  </>
);

// Private listings: sign in first, then choose postcodes. Postcodes apply on
// Enter or when leaving the field, so typing doesn't start a fetch each key.
const SaleKey = ({ postcodes, onPostcodes, google, sale }) => {
  const [draft, setDraft] = useState(postcodes);
  const [signInError, setSignInError] = useState(null);
  useEffect(() => setDraft(postcodes), [postcodes]);

  if (!google.ready) return <p className="aq-layer__note">Checking sign-in…</p>;

  if (!google.user) {
    return (
      <>
        <button
          type="button"
          className="aq-button"
          onClick={() =>
            signInWithGoogle().catch((err) => setSignInError(err.message))
          }
        >
          Sign in with Google
        </button>
        <p className="aq-layer__note">
          Private: listings only load for approved accounts.
          {signInError && ` Sign-in failed (${signInError}).`}
        </p>
      </>
    );
  }

  const apply = () => draft !== postcodes && onPostcodes(draft);
  return (
    <>
      <label className="aq-field">
        <span>Postcodes</span>
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          placeholder="e.g. 105, 107"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => e.key === "Enter" && apply()}
        />
      </label>
      <p className="aq-layer__note">
        <span className="aq-sale-house aq-sale-house--key" aria-hidden="true">
          <svg viewBox="0 0 16 16">
            <path d="M8 1.5 1.5 7.2V15h4.6v-4.4h3.8V15h4.6V7.2Z" />
          </svg>
        </span>{" "}
        {sale.error
          ? sale.error.message
          : sale.data
            ? `${sale.data.listings.length} on the map${
                sale.data.unplaced
                  ? `, ${sale.data.unplaced} without a known address`
                  : ""
              }. From fasteignir.is.`
            : "Loading listings…"}
      </p>
      <p className="aq-layer__note">
        {google.user.email} ·{" "}
        <button type="button" className="aq-link" onClick={signOutOfGoogle}>
          Sign out
        </button>
      </p>
    </>
  );
};

// Road weather/traffic stations are markers on the map; the notices have no
// coordinates, so they show here as a list grouped by Vegagerðin's regions
const RoadKey = ({ roads }) => {
  if (roads.error) {
    return (
      <p className="aq-layer__note">
        Could not load road data ({roads.error.message}).
      </p>
    );
  }
  if (!roads.data) {
    return <p className="aq-layer__note">Loading umferdin.is…</p>;
  }
  const { stations, notices } = roads.data;
  const icy = stations.filter(isIcy).length;
  const groups = groupNotices(notices);
  return (
    <>
      <p className="aq-layer__note">
        <span className="aq-road aq-road--key" aria-hidden="true">
          8°
        </span>{" "}
        Air temperature at each road station; chips turn blue when the road is
        freezing.
        {icy > 0 && (
          <>
            {" "}
            <span className="aq-mono">{icy}</span> freezing now.
          </>
        )}{" "}
        <a href={ROADS_SOURCE_URL} target="_blank" rel="noreferrer">
          umferdin.is
        </a>
      </p>
      {groups.length > 0 && (
        <div className="aq-road-notices">
          {groups.map(([region, items]) => (
            <div key={region} className="aq-road-notices__group">
              <p className="aq-road-notices__region">{region}</p>
              {items.map((n) => (
                <p key={n.id} className="aq-road-notices__item">
                  {n.text}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const LayerPanel = ({
  layers,
  setLayer,
  counts,
  stationCount,
  soilCount,
  soilError,
  google,
  sale,
  roads,
}) => {
  const { collapsed } = layers;

  return (
    <section className="aq-panel aq-panel--layers" aria-label="Map layers">
      <button
        type="button"
        className="aq-layers__toggle"
        aria-expanded={!collapsed}
        onClick={() => setLayer("collapsed", !collapsed)}
      >
        Layers
        <span className="aq-layers__chevron" aria-hidden="true">
          {collapsed ? "▸" : "▾"}
        </span>
      </button>

      {!collapsed && (
        <ul className="aq-layers">
          <Layer
            label="Air quality"
            meta={stationCount ? `${stationCount} stations` : null}
            checked={layers.air}
            onChange={(on) => setLayer("air", on)}
          >
            <SeverityKey counts={counts} />
            <Switch
              small
              checked={layers.drift}
              onChange={(on) => setLayer("drift", on)}
            >
              Drift (fair or worse)
            </Switch>
          </Layer>

          <Layer
            label="Wind"
            checked={layers.wind}
            onChange={(on) => setLayer("wind", on)}
          >
            <p className="aq-layer__note">
              Arrows point where the air is moving; longer means stronger.
            </p>
          </Layer>

          <Layer
            label="Pollen"
            meta={`${POLLEN_SITES.length} sites`}
            checked={layers.pollen}
            onChange={(on) => setLayer("pollen", on)}
          >
            <p className="aq-layer__note">
              <span className="aq-pollen aq-pollen--small" aria-hidden="true" />{" "}
              Counted April–September.{" "}
              {inPollenSeason()
                ? "Counts aren't published as a data feed yet."
                : "Out of season now."}{" "}
              <a href={POLLEN_LINKS.live} target="_blank" rel="noreferrer">
                Live counts
              </a>{" "}
              ·{" "}
              <a href={POLLEN_LINKS.forecast} target="_blank" rel="noreferrer">
                Forecast
              </a>
            </p>
          </Layer>

          <Layer
            label="Road noise"
            checked={layers.noise !== null}
            onChange={(on) => setLayer("noise", on ? "day" : null)}
          >
            {layers.noise && (
              <NoiseKey
                period={layers.noise}
                onPeriod={(period) => setLayer("noise", period)}
              />
            )}
          </Layer>

          <Layer
            label="Contaminated soil"
            meta={soilCount ? `${soilCount} sites` : null}
            checked={layers.soil}
            onChange={(on) => setLayer("soil", on)}
          >
            <p className="aq-layer__note">
              <span className="aq-soil" aria-hidden="true" />{" "}
              {soilError
                ? `Could not load the register (${soilError}).`
                : soilCount
                  ? "Known or suspected sites."
                  : "Loading UST's register…"}{" "}
              <a href={SOIL_SOURCE_URL} target="_blank" rel="noreferrer">
                Source
              </a>
            </p>
          </Layer>
          <Layer
            label="Road weather"
            meta={
              roads.data
                ? `${roads.data.stations.length} stations${
                    roads.data.notices.length
                      ? `, ${roads.data.notices.length} notices`
                      : ""
                  }`
                : null
            }
            checked={layers.roads}
            onChange={(on) => setLayer("roads", on)}
          >
            <RoadKey roads={roads} />
          </Layer>

          <Layer
            label="For sale"
            meta={sale.data ? `${sale.data.listings.length} homes` : null}
            checked={layers.sale}
            onChange={(on) => setLayer("sale", on)}
          >
            <SaleKey
              postcodes={layers.salePostcodes}
              onPostcodes={(value) => setLayer("salePostcodes", value)}
              google={google}
              sale={sale}
            />
          </Layer>
        </ul>
      )}
    </section>
  );
};
